import { instant } from './domain/canonical';
import type { RecordItem } from './server/types';

export type PublicationTiming = 'upcoming' | 'overdue' | 'unscheduled' | 'awaiting_outcome' | 'completed';
const completedStates = new Set(['published', 'confirmed', 'cancelled']);
const unresolvedStates = new Set(['outcome_unknown', 'provider_accepted', 'accepted_by_provider', 'processing', 'dispatching', 'scheduled_provider']);

export function validTimeZone(timeZone: string): boolean {
  try { new Intl.DateTimeFormat('en-US', { timeZone }).format(0); return true; }
  catch { return false; }
}

/** Resolve a UTC/offset instant to a calendar date in one explicitly selected zone. */
export function calendarDate(value: unknown, timeZone: string): string | null {
  if (!validTimeZone(timeZone)) throw new RangeError('A valid calendar timezone is required.');
  const timestamp = instant(value);
  if (timestamp === null) return null;
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(timestamp);
  const part = (name: string) => parts.find(entry => entry.type === name)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

function dateOnly(value: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || instant(`${value}T00:00:00Z`) === null) throw new RangeError('A real YYYY-MM-DD calendar date is required.');
  return new Date(`${value}T00:00:00Z`);
}

/** Calendar-day arithmetic, never elapsed 24-hour jumps across local DST boundaries. */
export function moveCalendarDate(value: string, days: number): string {
  if (!Number.isInteger(days)) throw new RangeError('Calendar navigation needs whole days.');
  const date = dateOnly(value);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function weekDates(anchor: string): string[] {
  const day = dateOnly(anchor).getUTCDay();
  const monday = moveCalendarDate(anchor, -((day + 6) % 7));
  return Array.from({ length: 7 }, (_, index) => moveCalendarDate(monday, index));
}

export function publicationTiming(record: RecordItem, now: string): PublicationTiming {
  if (completedStates.has(record.status)) return 'completed';
  if (record.status === 'outcome_unknown') return 'awaiting_outcome';
  const scheduled = instant(record.data.scheduledAt), current = instant(now);
  if (current === null) throw new RangeError('A valid current instant is required.');
  if (scheduled === null) return 'unscheduled';
  if (scheduled < current) return unresolvedStates.has(record.status) ? 'awaiting_outcome' : 'overdue';
  return 'upcoming';
}

export function sortPublications(records: RecordItem[]): RecordItem[] {
  return [...records].sort((left, right) => {
    const a = instant(left.data.scheduledAt), b = instant(right.data.scheduledAt);
    if (a === null && b === null) return left.title.localeCompare(right.title) || left.id.localeCompare(right.id);
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b || left.id.localeCompare(right.id);
  });
}

export function publicationWeek(records: RecordItem[], anchor: string, timeZone: string) {
  const dates = weekDates(anchor);
  const days = dates.map(date => ({ date, records: [] as RecordItem[] }));
  const byDate = new Map(days.map(day => [day.date, day.records]));
  const outsideWeek: RecordItem[] = [], unscheduled: RecordItem[] = [];
  for (const record of sortPublications(records)) {
    const date = calendarDate(record.data.scheduledAt, timeZone);
    if (date === null) unscheduled.push(record);
    else if (byDate.has(date)) byDate.get(date)!.push(record);
    else outsideWeek.push(record);
  }
  return { days, outsideWeek, unscheduled };
}

export function simultaneousPublications(records: RecordItem[]): Set<string> {
  const groups = new Map<string, string[]>();
  for (const record of records) {
    const stamp = instant(record.data.scheduledAt), account = record.data.accountId, platform = record.data.platform;
    if (stamp === null || typeof account !== 'string' || !account.trim() || typeof platform !== 'string' || completedStates.has(record.status)) continue;
    const key = JSON.stringify([platform.toLowerCase(), account, stamp]);
    groups.set(key, [...(groups.get(key) ?? []), record.id]);
  }
  return new Set([...groups.values()].filter(ids => ids.length > 1).flat());
}
